"use client";

import { Tooltip } from "flowbite-react";
import { RichText } from "@/components/RichText";
import { useProjectText } from "@/features/project/useProjectText";
import type { POIPageField } from "@/types";
import type { FieldValue } from "../../poiFields";

/**
 * One configured field of a POI: an icon, its value, and — when the tenant asked
 * for a label — a tooltip naming the field.
 *
 * The tooltip is the label, rather than text beside the value, because the fields
 * block is a dense inline run of icons and values; that's the legacy design and
 * it's why `label` is a boolean in the config rather than a string.
 */
export function POIField({
  field,
  value,
}: {
  field: POIPageField;
  value: FieldValue;
}) {
  const t = useProjectText();

  if (!value) return null;

  const content = (
    <>
      {field.icon && (
        <i
          className={`icon ic-${field.icon} text-primary text-[18px] w-5 shrink-0`}
        />
      )}
      {value !== true && (
        <span className="text-sm">
          <RichText html={value} />
        </span>
      )}
    </>
  );

  return (
    <div className="flex items-center gap-1">
      {field.label ? (
        <Tooltip
          content={t(field.field)}
          placement="top"
          arrow
          className="!bg-primary dynamic-text !rounded-lg !px-4 !py-2 text-xs shadow-lg !duration-100"
          theme={{ target: "inline-flex items-center gap-2" }}
        >
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </div>
  );
}
